import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import "@xterm/xterm/css/xterm.css";
import { useEffect, useRef, useState } from "react";

import type { SessionApi } from "../lib/api";

/** Terminal wired to the session's pty, over the recording gateway.
 *
 * Input goes up as JSON text frames, output comes back as raw binary, so a
 * multi-byte character split across two reads is reassembled by xterm rather
 * than mangled by an early decode.
 *
 * It reconnects on its own. A control-plane restart leaves the browser holding a
 * socket that is dead but not closed: the terminal looks fine and swallows
 * everything typed into it. The heartbeat is what turns that into a detectable
 * failure, and the backoff is what gets the candidate working again without
 * knowing to reload the page.
 */
const HEARTBEAT_MS = 10_000;
const SILENCE_LIMIT_MS = 28_000;
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000];

export function TerminalPane({
  api,
  onStatus,
}: {
  api: SessionApi;
  onStatus: (connected: boolean) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const term = new Terminal({
      fontFamily: 'SF Mono, Menlo, Consolas, "Liberation Mono", monospace',
      fontSize: 13,
      cursorBlink: true,
      theme: { background: "#181818", foreground: "#cccccc" },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    fit.fit();

    let socket: WebSocket | null = null;
    let attempt = 0;
    let disposed = false;
    let heartbeat: number | undefined;
    let retry: number | undefined;
    let lastSeen = Date.now();

    const resize = new ResizeObserver(() => {
      fit.fit();
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
      }
    });
    resize.observe(host);

    const typed = term.onData((data) => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "input", data }));
      }
    });

    function scheduleRetry(quiet = false) {
      if (disposed) return;
      const wait = quiet
        ? BACKOFF_MS[BACKOFF_MS.length - 1]
        : BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      attempt += 1;
      if (!quiet) term.writeln(`\r\n\x1b[2m[reconnecting in ${Math.round(wait / 1000)}s…]\x1b[0m`);
      retry = window.setTimeout(connect, wait);
    }

    function connect() {
      if (disposed) return;
      const ws = new WebSocket(api.ptyUrl(term.cols, term.rows));
      socket = ws;
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        lastSeen = Date.now();
        onStatus(true);
        setError(null);
        if (attempt > 0) {
          // Say it plainly: the container and their files are untouched, but the
          // shell is new, so a forgotten `cd` would otherwise be baffling.
          term.writeln("\r\n\x1b[2m[reconnected — this is a fresh shell; your files are as you left them]\x1b[0m");
        }
        attempt = 0;
        ws.send(JSON.stringify({ type: "resize", cols: term.cols, rows: term.rows }));
      };

      ws.onmessage = (event) => {
        lastSeen = Date.now();
        if (typeof event.data === "string") return; // pong, nothing to render
        term.write(new Uint8Array(event.data as ArrayBuffer));
      };

      ws.onclose = (event) => {
        onStatus(false);
        // 1008 is the gateway refusing: paused, or submitted. Keep polling at
        // the slow end so the shell comes back by itself on resume, but say
        // nothing — a pause should not fill the terminal with retry notices.
        if (!disposed) scheduleRetry(event.code === 1008);
      };

      ws.onerror = () => setError("lost the connection to the workspace terminal");
    }

    heartbeat = window.setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "ping" }));
        // A socket whose peer vanished stays "open" until TCP gives up, which can
        // take minutes. Silence is the only timely signal we get.
        if (Date.now() - lastSeen > SILENCE_LIMIT_MS) socket.close();
      }
    }, HEARTBEAT_MS);

    connect();

    return () => {
      disposed = true;
      window.clearInterval(heartbeat);
      window.clearTimeout(retry);
      resize.disconnect();
      typed.dispose();
      socket?.close();
      term.dispose();
    };
  }, [api, onStatus]);

  return (
    <section className="terminal-area" aria-label="Terminal">
      <div className="pane-title">Terminal</div>
      {error && <div className="error">{error}</div>}
      <div className="terminal-host" ref={hostRef} />
    </section>
  );
}
